import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

const statusLabels = {
  "🟢": "Confirmed",
  "🟡": "Needs Confirmation",
  "🔴": "Rule Conflict"
};

// Coerce any value we show in <option> to a string label
const toLabel = (v) => {
  if (typeof v === "string") return v;
  if (v == null) return "";
  if (typeof v === "object") {
    if (typeof v.name === "string") return v.name;
    if (typeof v.label === "string") return v.label;
    if (typeof v.value === "string") return v.value;
    try { return String(v); } catch { return ""; }
  }
  return String(v);
};


export default function TransactionRowStyled({
  isMobile = false,
  accountNameById = null,
  onOpenRuleEditor,
  onOpenReassign,
  onOpenConfirmAndRule, // ✅ NEW
  tx,
  idx,
  onConfirm,
  onEdit,
  onDelete,
  onMemoSave,
  onShowMiniModal,
  children,
  onCategoryChange,
  onCancelDraft,
  budgets,
  columnStyles,
  singleAmount,
  compactSelects,
  editLabel = "Edit"
}) {
  // First: pull fields off tx so mobile can use them safely
  const {
    vendor,
    income,
    expense,
    amount,
    date,
    category,
    subcategory,
    status,
    showConfirm,
    memo,
    reason,
    id,
    pendingDraft,
    originalCategory,
    originalSubcategory,
    hasUnsavedDraft,
  } = tx;

  const hasDraftChange =
    Boolean(pendingDraft) &&
    (toLabel(category).trim() !== toLabel(originalCategory).trim() ||
      toLabel(subcategory).trim() !== toLabel(originalSubcategory).trim());

  // Shared state used by both mobile and desktop render paths
  const [isCatMenuOpen, setIsCatMenuOpen] = useState(false);

  const [mobileExpanded, setMobileExpanded] = useState(false); // ✅ mobile details toggle

  // ─────────────── Mobile-only compact row ───────────────
  if (isMobile) {
    const amountVal =
      typeof amount === "number"
        ? amount
        : (typeof income === "number"
            ? income
            : (typeof expense === "number" ? -Math.abs(expense) : 0));

    const isNeg = amountVal < 0;
    const POS = "#00B074";
    const NEG = "#c62828";
    const needsConfirm = !!showConfirm;

    return (
      <div
        style={{
          borderBottom: "1px solid #eee",
          padding: "8px 8px",
          background: needsConfirm ? "#fff9e6" : "#fff"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4, gap: 6 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: "0.95rem",        // ⬅️ slightly smaller
              minWidth: 0,
              flex: 1,
              maxWidth: "65%",            // ⬅️ cap width so amount/actions fit
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
            title={vendor}
          >
            {vendor}
          </div>
          <div style={{ fontWeight: 700, color: isNeg ? NEG : POS }}>
            {amountVal.toLocaleString(undefined, { style: "currency", currency: "USD" })}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6, gap: 8 }}>
          <div style={{ fontSize: "0.9rem", color: "#444", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {category || "Uncategorized"}{subcategory ? ` • ${subcategory}` : ""}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {needsConfirm && typeof onConfirm === "function" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirm(); // ✅ Confirm = confirm transaction only (no rule)
                }}
                style={{
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 8,
                  border: "1px solid #00B074",
                  background: "#00B074",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                }}
              >
                Confirm
              </button>
            )}

            <button
              aria-label="More actions"
              onClick={(e) => {
                e.stopPropagation();
                setIsCatMenuOpen((v) => !v);
              }}
              style={{
                position: "relative",
                width: 32,
                height: 28,
                border: "1px solid #ddd",
                background: "#fff",
                borderRadius: 8,
                padding: 0,
                display: "inline-flex",
                alignItems: "flex-end",
                justifyContent: "center",
                color: "#111",
                cursor: "pointer",
              }}
            >
              <span style={{ position: "relative", bottom: 2 }}>⋯</span>
            </button>
          </div>
        </div>

        <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: "0.8rem", color: "#666" }}>
            {statusLabels[status] || ""}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setMobileExpanded((v) => !v);
            }}
            style={{
              border: "none",
              background: "none",
              color: "#444",
              fontSize: "0.85rem",
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            {mobileExpanded ? "Hide details" : "Transaction details"}
          </button>
        </div>

        {mobileExpanded && (() => {
          const acctId = tx?.accountId || tx?.account_id || "";
          const acctLabel =
            (accountNameById && acctId && accountNameById[acctId]) ||
            tx?.accountName ||
            tx?.account_name ||
            "";

          const bankCat =
            tx?.bankCategory ||
            tx?.plaidCategory ||
            "—";

          return (
            <div style={{ marginTop: 6, fontSize: "0.85rem", color: "#444", lineHeight: 1.35 }}>
              {date ? <div><b>Date:</b> {date}</div> : null}
              <div><b>Account:</b> {acctLabel || "—"}</div>
              <div><b>Bank category:</b> {bankCat}</div>
              {memo ? <div style={{ marginTop: 4 }}><b>Memo:</b> {memo}</div> : null}
              {reason ? <div style={{ marginTop: 4 }}><b>Note:</b> {reason}</div> : null}
            </div>
          );
        })()}

        {isCatMenuOpen && createPortal(
          <div
            // Overlay to enable outside-tap close
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 20000,
              background: "rgba(0,0,0,0.15)" // subtle dim; change to "transparent" if you prefer no dim
            }}
            onClick={() => setIsCatMenuOpen(false)}
          >
            <div
              data-cat-menu="1"
              onClick={(e) => e.stopPropagation()} // keep menu open when tapping inside
              style={{
                position: "absolute",
                bottom: 12,
                left: 12,
                right: 12,
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
                overflow: "hidden"
              }}
            >
              <div style={{ padding: "10px 12px", borderBottom: "1px solid #eee", fontWeight: 700 }}>
                Actions
              </div>

              {/* ✅ Mobile: show Transaction details in kebab */}
                <button
                  onClick={() => {
                    setIsCatMenuOpen(false);
                    setMobileExpanded(true); // open details panel
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    border: "none",
                    background: "#fff",
                    cursor: "pointer",
                    fontWeight: 700,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  Transaction details
                </button>

              {needsConfirm && typeof onOpenConfirmAndRule === "function" && (
                <button
                  onClick={() => {
                    setIsCatMenuOpen(false);
                    onOpenConfirmAndRule(); // ✅ opens confirm rule modal flow
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#111",
                  }}
                >
                  Confirm and set rule as suggested
                </button>
              )}

              <button
                onClick={() => {
                  setIsCatMenuOpen(false);
                  onOpenRuleEditor && onOpenRuleEditor(); // ✅ “Make a new rule”
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "12px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#111",
                }}
              >
                Create a new rule
              </button>

              <button
                onClick={() => {
                  setIsCatMenuOpen(false);
                  onOpenReassign && onOpenReassign();
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "12px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#111",
                }}
              >
                Reassign transaction only without rule
              </button>

              {typeof onDelete === "function" && (
                <button
                  onClick={() => {
                    setIsCatMenuOpen(false);
                    onDelete(); // ✅ parent handles confirmation modal
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    background: "none",
                    border: "none",
                    color: "#c62828",
                    cursor: "pointer",
                    borderTop: "1px solid #eee",
                  }}
                >
                  Delete transaction
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  const [memoText, setMemoText] = useState(memo || "");
  const inputRef = useRef();
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = inputRef.current.scrollHeight + "px";
    }
  }, [memoText]);

  // ---- Category dropdown: grouping + expand/collapse state ----
  const [expandedSections, setExpandedSections] = useState({
    income: false,
    expense: false,
    savings: false,
    transfers: false
  });
  const catWrapRef = useRef(null);

  const catButtonRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  // Match overlay typography to the visible native <select>
const catSelectRef = useRef(null);
const [catSelectStyle, setCatSelectStyle] = useState({
  fontFamily: "",
  fontSize: "20",
  optionHeight: 28, // fallback
});

const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
const [desktopExpanded, setDesktopExpanded] = useState(false);
const actionBtnRef = useRef(null);
const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, right: 0 });

useEffect(() => {
  const el = catSelectRef.current;
  if (!el) return;

  const update = () => {
    const cs = window.getComputedStyle(el);
    const h = Math.round(el.getBoundingClientRect().height) || 28;
    setCatSelectStyle({
      fontFamily: cs.fontFamily || "",
      fontSize: cs.fontSize || "",
      optionHeight: h,
    });
  };

  update();

  // keep in sync with layout/font changes
  const ro = new ResizeObserver(update);
  ro.observe(el);
  window.addEventListener("resize", update);
  return () => {
    ro.disconnect();
    window.removeEventListener("resize", update);
  };
}, []);

  const recalcMenuPos = () => {
    const wrap = catWrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  const recalcActionMenuPos = () => {
    const btn = actionBtnRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();

    const MENU_W = 280;
    const MARGIN = 12;

    // Use viewport coords (we’ll render the menu as position: fixed)
    let left = rect.left;

    // Clamp inside viewport
    const maxLeft = window.innerWidth - MENU_W - MARGIN;
    if (left > maxLeft) left = maxLeft;
    if (left < MARGIN) left = MARGIN;

    const top = rect.bottom + 6;

    setActionMenuPos({ top, left });
  };

  // Recalculate on open, scroll, or resize (so it sticks to the button)
  useEffect(() => {
    if (!isCatMenuOpen) return;
    recalcMenuPos();
    const onScroll = () => recalcMenuPos();
    const onResize = () => recalcMenuPos();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isCatMenuOpen]);

  useEffect(() => {
    function onEsc(e) {
      if (e.key === "Escape") setIsCatMenuOpen(false);
    }
    if (isCatMenuOpen) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [isCatMenuOpen]);

  useEffect(() => {
    function closeOnOutside(e) {
      const menu = document.querySelector('[data-cat-menu="1"]');
      const wrap = catWrapRef.current;
      const insideWrap = !!wrap && wrap.contains(e.target);
      const insideMenu = !!menu && menu.contains(e.target);
      if (!insideWrap && !insideMenu) setIsCatMenuOpen(false);
    }
    if (isCatMenuOpen) document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [isCatMenuOpen]);

  useEffect(() => {
    if (!isActionMenuOpen) return;
    recalcActionMenuPos();
    const onScroll = () => recalcActionMenuPos();
    const onResize = () => recalcActionMenuPos();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isActionMenuOpen]);

  useEffect(() => {
    function onEsc(e) {
      if (e.key === "Escape") setIsActionMenuOpen(false);
    }
    if (isActionMenuOpen) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [isActionMenuOpen]);

  useEffect(() => {
    function closeOnOutside(e) {
      const menu = document.querySelector('[data-action-menu="1"]');
      const btn = actionBtnRef.current;
      const insideBtn = !!btn && btn.contains(e.target);
      const insideMenu = !!menu && menu.contains(e.target);
      if (!insideBtn && !insideMenu) setIsActionMenuOpen(false);
    }
    if (isActionMenuOpen) document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [isActionMenuOpen]);

  // Build grouped/ordered sections for the dropdown (alphabetical within each)
  const groupedSections = useMemo(() => {
    const list = Array.isArray(budgets) ? budgets : [];

    const sortCat = (a, b) =>
      (a?.category || "").localeCompare(b?.category || "", undefined, { sensitivity: "base" });

    const isIncomeType    = (b) => (b?.type || "").toLowerCase() === "income";
    const isSavingsType   = (b) => (b?.type || "").toLowerCase() === "savings";
    const isTransfersName = (b) => (b?.category || "") === "Transfers & Payments (Not in Budget)";
    const isNonBudgetType = (b) => (b?.type || "").toLowerCase() === "non-budget";

    const income    = list.filter(isIncomeType).sort(sortCat);
    const transfers = list.filter(b => isTransfersName(b) || isNonBudgetType(b)).sort(sortCat);
    const savings   = list.filter(isSavingsType).sort(sortCat);
    const expense   = list
      .filter(b => !isIncomeType(b) && !isSavingsType(b) && !isTransfersName(b) && !isNonBudgetType(b))
      .sort(sortCat);

    return { income, expense, savings, transfers };
  }, [budgets]);

  const sectionOrder = [
    { key: "income",    label: "Income Section" },
    { key: "expense",   label: "Expense Section" },
    { key: "savings",   label: "Savings Section" },
    { key: "transfers", label: "Non-Budget Items" }
  ];

  const gapVal =
    typeof rowGap === "number" || typeof rowGap === "string"
      ? rowGap
      : "0.5rem";

  const rowStyle = {
    display: "flex",
    alignItems: "flex-start",
    padding: "0.75rem 0.5rem",
    gap: gapVal, // ⬅️ was: rowGap
    fontSize: "0.95rem",
    backgroundColor: status === "🔴" ? "#ffe5e5" : status === "🟡" ? "#fffdea" : idx % 2 === 0 ? "#fafafa" : "white",
    borderBottom: "1px solid #eee",
    borderLeft: status === "🔴" ? "4px solid red" : status === "🟡" ? "4px solid #F4C542" : "4px solid transparent",
    color: "#222",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    cursor: "pointer"
  };

  const colStyle = {
  flex: 1,
  minWidth: "100px",
  textAlign: "center"
};

  const vendorStyle = { ...colStyle, minWidth: "120px", textAlign: "left", paddingLeft: "8px" };
  const categoryStyle = { ...colStyle, minWidth: "150px" };
  const subcategoryStyle = { ...colStyle, minWidth: "200px" };
  const memoStyle = { ...colStyle, minWidth: "200px", textAlign: "left", paddingLeft: "8px" };



  const handleMemoSave = (e) => {
  e.stopPropagation();
  if (typeof onMemoSave === "function") {
    onMemoSave(id, memoText);
  }
  };

return (
  <>
  <div
    style={rowStyle}
    onMouseEnter={(e) => {
      if (isCatMenuOpen) return; // don’t shift the row while menu is open
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.05)";
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.zIndex = "1";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = "none";
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.zIndex = "1";
    }}
  >
    <div
      style={{
        ...columnStyles[1],
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }}
      title={vendor} // shows full vendor name on hover
    >
      {vendor}
    </div>
      {singleAmount ? (
        <>
          {(() => {
            // We pass signed amount into `income` (and leave `expense` null) from Step 3
            const raw = (typeof amount === "number")
                  ? amount
                  : (typeof income === "number"
                      ? income
                      : (typeof expense === "number" ? -Math.abs(expense) : null));
            if (raw == null) {
              return (
                <>
                  <div style={columnStyles[2]}></div>
                  <div style={columnStyles[3]}></div>
                </>
              );
            }
            const isNeg = raw < 0;
            // Brand colors
            const POS = "#00B074";
            const NEG = "#c62828";

            return (
              <>
                <div style={{ ...columnStyles[2], color: isNeg ? NEG : POS, fontWeight: 600 }}>
                  {raw.toLocaleString(undefined, { style: "currency", currency: "USD" })}
                </div>
                {/* keep grid alignment; column 3 stays present but is zero-width via columnStyles */}
                <div style={columnStyles[3]}></div>
              </>
            );
          })()}
        </>
      ) : (
      <>
        <div style={{ ...columnStyles[2], color: "green" }}>
          {income != null ? `$${income.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ""}
        </div>
        <div style={{ ...columnStyles[3], color: "red" }}>
          {expense != null ? `-$${Math.abs(expense).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : ""}
        </div>
      </>
    )}
    <div style={columnStyles[4]}>{date}</div>
    <div style={columnStyles[5]}>
      <select
        value={category}
        onChange={(e) => {
          if (e.target.value === "__new__") {
            onShowMiniModal("category", "", id);
          } else {
            onCategoryChange(id, e.target.value, "category");
          }
        }}
        style={{
          // same widths you used before to match the Subcategory select
          minWidth: compactSelects ? "130px" : "160px",
          maxWidth: compactSelects ? "130px" : "160px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        <option value="">Select</option>

        {/* 🔹 Income Section */}
        {groupedSections.income?.length > 0 && (
          <optgroup label="Income Section">
            {groupedSections.income.map((b) => (
              <option key={`inc-${toLabel(b.category)}`} value={toLabel(b.category)}>
                {toLabel(b.category)}
              </option>
            ))}
          </optgroup>
        )}

        {/* 🔹 Expense Section (alphabetical) */}
        {groupedSections.expense?.length > 0 && (
          <optgroup label="Expense Section">
            {groupedSections.expense.map((b) => (
              <option key={`exp-${toLabel(b.category)}`} value={toLabel(b.category)}>
                {toLabel(b.category)}
              </option>
            ))}
          </optgroup>
        )}

        {/* 🔹 Savings Section (alphabetical) */}
        {groupedSections.savings?.length > 0 && (
          <optgroup label="Savings Section">
            {groupedSections.savings.map((b) => (
              <option key={`sav-${toLabel(b.category)}`} value={toLabel(b.category)}>
                {toLabel(b.category)}
              </option>
            ))}
          </optgroup>
        )}

        {/* 🔹 Non-Budget Items */}
        {groupedSections.transfers?.length > 0 && (
          <optgroup label="Non-Budget Items">
            {groupedSections.transfers.map((b) => (
              <option key={`trn-${toLabel(b.category)}`} value={toLabel(b.category)}>
                {toLabel(b.category)}
              </option>
            ))}
          </optgroup>
        )}

        {/* ➕ Keep your original add-new option */}
        <option value="__new__">+ New Category</option>
      </select>

      {(() => {
        const isNonBudget =
          Array.isArray(budgets) &&
          budgets.some(
            (b) =>
              toLabel(b.category) === toLabel(category) &&
              (b?.type || "").toLowerCase() === "non-budget"
          );
        return (
          isNonBudget && (
            <div style={{ fontSize: "0.75rem", color: "#999", marginTop: "0.25rem" }}>
              Not included in budget
            </div>
          )
        );
      })()}
    </div>

      <div style={columnStyles[6]}>
        <select
          value={subcategory}
          onChange={(e) => {
            if (e.target.value === "__new__") {
              onShowMiniModal("subcategory", category, id);
            } else {
              onCategoryChange(id, e.target.value, "subcategory");
            }
          }}
          style={{
            minWidth: "160px",
            maxWidth: "160px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          <option value="">Select</option>
          {(budgets.find((b) => toLabel(b.category) === toLabel(category))?.subcategories || [])
            .map((s, i) => {
              // s can be a string or object; s.name might itself be an object in your data
              const label = toLabel(typeof s === "string" ? s : (s?.name ?? s));
              return <option key={i} value={label}>{label}</option>;
            })}
          {category && <option value="__new__">+ New Subcategory</option>}
        </select>
      </div>
      <div style={columnStyles[7]}>
        <textarea
          ref={inputRef}
          rows={1}
          
          value={memoText}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setMemoText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleMemoSave(e);
              inputRef.current?.blur();
            }
          }}
          style={{ width: "100%", minWidth: "160px", maxWidth: "160px", whiteSpace: "pre-wrap", wordWrap: "break-word", resize: "none", overflow: "hidden", height: "auto" }}
        />
        {memoText !== (memo || "") && (
          <button
            onClick={(e) => { handleMemoSave(e); inputRef.current?.blur(); }}
            style={{
              background: "none",
              border: "none",
              color: "#00B074",
              cursor: "pointer",
              textDecoration: "underline",
              fontSize: "0.85rem",
              marginLeft: "6px"
            }}
          >
            Save
          </button>
        )}
      </div>
      <div
        style={columnStyles[8]}
        title={`${statusLabels[status] || ""}${reason ? ` — ${reason}` : (showConfirm ? " — Suggested match: Please confirm or edit." : "")}`}
      >
        {status}
      </div>
      <div style={{ ...columnStyles[9], display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
        {showConfirm && typeof onConfirm === "function" && (
          <button
            style={confirmButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              onConfirm(); // ✅ confirm transaction only (no rule)
            }}
            title="Confirm this transaction only"
          >
            Confirm
          </button>
        )}

        {hasUnsavedDraft && typeof onCancelDraft === "function" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancelDraft(id);
            }}
            style={{
              height: 30,
              padding: "0 10px",
              borderRadius: 10,
              border: "1px solid #ddd",
              background: "#fff",
              cursor: "pointer",
              fontWeight: 700,
            }}
            title="Discard unsaved changes"
          >
            Cancel
          </button>
        )}

        <button
          ref={actionBtnRef}
          aria-label="More actions"
          onClick={(e) => {
            e.stopPropagation();
            setIsActionMenuOpen((v) => !v);
          }}
          style={{
            width: 34,
            height: 30,
            borderRadius: 10,
            border: "1px solid #ddd",
            background: "#fff",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "flex-end",
            justifyContent: "center",
            padding: 0
          }}
          title="More"
        >
          <span style={{ position: "relative", bottom: 3 }}>⋯</span>
        </button>

        {isActionMenuOpen &&
          createPortal(
            <div
              data-action-menu="1"
              style={{
                position: "fixed",
                top: actionMenuPos.top,
                left: actionMenuPos.left,
                zIndex: 20000,
                width: 280,
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                overflow: "hidden",
                border: "1px solid #eee"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: "10px 12px", borderBottom: "1px solid #eee", fontWeight: 700 }}>
                Actions
              </div>

              {showConfirm && typeof onOpenConfirmAndRule === "function" && (
                <button
                  onClick={() => {
                    setIsActionMenuOpen(false);
                    onOpenConfirmAndRule(); // ✅ opens confirm+rule modal
                  }}
                  style={menuBtnStyle}
                >
                  Confirm and set rule as suggested
                </button>
              )}

              <button
                onClick={() => {
                  setIsActionMenuOpen(false);
                  onOpenRuleEditor?.();
                }}
                style={menuBtnStyle}
              >
                Create a new rule
              </button>

              <button
                onClick={() => {
                  setIsActionMenuOpen(false);
                  onOpenReassign?.();
                }}
                style={menuBtnStyle}
              >
                Reassign transaction only without rule
              </button>

              {typeof onDelete === "function" && (
                <button
                  onClick={() => {
                    setIsActionMenuOpen(false);
                    onDelete();
                  }}
                  style={{ ...menuBtnStyle, color: "#c62828", borderTop: "1px solid #eee" }}
                >
                  Delete transaction
                </button>
              )}

              <div style={{ padding: 10, borderTop: "1px solid #eee" }}>
                <button
                  onClick={() => setDesktopExpanded((v) => !v)}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "#f6f6f6",
                    borderRadius: 10,
                    padding: "10px 12px",
                    cursor: "pointer",
                    fontWeight: 700
                  }}
                >
                  {desktopExpanded ? "Hide transaction details" : "Transaction details"}
                </button>
              </div>
            </div>,
            document.body
          )}
      </div>
      {children}
    </div>
    {desktopExpanded && (() => {
      const acctId = tx?.accountId || tx?.account_id || "";
      const acctLabel =
        (accountNameById && acctId && accountNameById[acctId]) ||
        tx?.accountName ||
        tx?.account_name ||
        "—";

      const bankCat =
        tx?.bankCategory ||
        tx?.plaidCategory ||
        "—";

      return (
        <div
          style={{
            padding: "10px 12px 14px",
            borderBottom: "1px solid #eee",
            background: "#fff",
            fontSize: "0.9rem",
            color: "#333",
            marginLeft: 4
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
            <div><b>Account:</b> {acctLabel}</div>
            {date ? <div><b>Date:</b> {date}</div> : null}

            <div><b>Bank category:</b> {bankCat}</div>
            {tx?.matchedSource ? <div><b>Match source:</b> {tx.matchedSource}</div> : null}

            {memo ? <div style={{ gridColumn: "1 / span 2" }}><b>Memo:</b> {memo}</div> : null}
            {reason ? <div style={{ gridColumn: "1 / span 2" }}><b>Note:</b> {reason}</div> : null}
          </div>
        </div>
      );
    })()}
    </>
  );
}

const menuBtnStyle = {
  width: "100%",
  textAlign: "left",
  padding: "12px",
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "#111",
};

const confirmButtonStyle = {
  background: "#00B074",
  border: "none",
  color: "white",
  cursor: "pointer",
  borderRadius: "4px",
  fontSize: "0.85rem",
  padding: "4px 10px"
};

const editButtonStyle = {
  background: "none",
  border: "none",
  color: "#00B074",
  cursor: "pointer",
  textDecoration: "underline",
  fontSize: "0.9rem",
  padding: 0
};